import React from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { AnimatedSection, AnimatedTableRow } from '../../components/animations/MotionContainers'
import { API_REQUESTS_PAUSED } from '../../data'
import { getApplications } from '../../services/admin/applicationsApi'
import { getPermissions } from '../../services/admin/permissionsApi'
import { getRoles } from '../../services/admin/rolesApi'
import { createUser, deleteUser, getUsers, updateUser } from '../../services/admin/usersApi'
import { formatDateTime, getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'

const emptyCreateUserForm = {
  username: '',
  email: '',
  displayName: '',
  password: '',
  active: true,
  mustChangePassword: false,
  applications: [],
  roleIds: [],
  permissions: [],
}

const mapApiUser = (user) => ({
  id: user.id,
  name: user.displayName || user.name || user.username,
  username: user.username,
  email: user.email || '-',
  status: user.active === false ? 'Inactivo' : 'Activo',
  role: user.roles?.[0]?.role?.name || user.roles?.[0]?.name || user.roles?.[0]?.code || 'Sin rol',
  apps: `${user.applications?.length || 0} apps`,
  lastLogin: formatDateTime(user.lastLoginAt),
  icon: <PersonRoundedIcon />,
  raw: user,
})

const getUniqueOptions = (values) => (
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b))
)

const getUniqueById = (items) => {
  const optionMap = new Map()
  items.filter(Boolean).forEach((item) => {
    const id = item.id || item.applicationId || item.roleId || item.permissionId
    if (id && !optionMap.has(String(id))) optionMap.set(String(id), item)
  })
  return [...optionMap.values()]
}

const getUserApplicationCodes = (user) => (
  (user.raw?.applications || []).map((application) => application.code || application.application?.code).filter(Boolean)
)

const getUserApplications = (users) => getUniqueById(users.flatMap((user) => user.raw?.applications || []))

const getUserRoles = (users) => getUniqueById(users.flatMap((user) => user.raw?.roles || []))

const getUserPermissions = (users) => getUniqueById(users.flatMap((user) => user.raw?.permissions || []))

const getOptionLabel = (option) => option.name || option.displayName || option.code || option.username || option.id

const toDateInputValue = (value) => {
  if (!value || value === 'string') return ''
  return String(value).slice(0, 10)
}

const getApplicationAssignment = (application) => application.assignment || application.userApplication || {}

const getApplicationId = (application) => String(application.applicationId || application.application?.id || application.id)

const getRoleId = (role) => String(role.roleId || role.role?.id || role.id)

const getPermissionId = (permission) => String(permission.permissionId || permission.permission?.id || permission.id)

const buildUserFormFromUser = (user) => ({
  username: user.username || '',
  email: user.email === '-' ? '' : user.email || '',
  displayName: user.displayName || user.name || '',
  password: '',
  active: user.active !== false,
  mustChangePassword: Boolean(user.mustChangePassword),
  applications: (user.applications || []).map((application) => {
    const assignment = getApplicationAssignment(application)
    return {
      applicationId: getApplicationId(application),
      active: assignment.active ?? application.active !== false,
      validFrom: toDateInputValue(assignment.validFrom),
      validUntil: toDateInputValue(assignment.validUntil),
    }
  }),
  roleIds: (user.roles || []).map(getRoleId),
  permissions: (user.permissions || []).map((permission) => ({
    permissionId: getPermissionId(permission),
    effect: permission.effect || 'ALLOW',
  })),
})

const buildUserPayload = (form, includePassword) => {
  const payload = {
    username: form.username.trim(),
    displayName: form.displayName.trim(),
    active: form.active,
    mustChangePassword: form.mustChangePassword,
    applications: form.applications.map((application) => ({
      applicationId: application.applicationId,
      active: application.active,
      ...(application.validFrom ? { validFrom: application.validFrom } : {}),
      ...(application.validUntil ? { validUntil: application.validUntil } : {}),
    })),
    roles: form.roleIds.map((roleId) => ({ roleId })),
    permissions: form.permissions.map((permission) => ({
      permissionId: permission.permissionId,
      effect: permission.effect,
    })),
  }

  if (form.email.trim()) payload.email = form.email.trim()
  if (includePassword) payload.password = form.password
  return payload
}

const validateUserForm = (form, requirePassword) => {
  if (!form.username.trim()) return 'El usuario es obligatorio.'
  if (!form.displayName.trim()) return 'El nombre visible es obligatorio.'
  if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    return 'El correo electrónico no tiene un formato válido.'
  }
  if ((requirePassword || form.password) && form.password.length < 8) {
    return 'La contraseña debe tener al menos 8 caracteres.'
  }
  return ''
}

const filterUsers = (users, filters) => {
  const search = filters.search.trim().toLowerCase()

  return users.filter((user) => {
    const searchableText = [
      user.name,
      user.username,
      user.email,
    ].join(' ').toLowerCase()
    const matchesSearch = !search || searchableText.includes(search)
    const matchesStatus = filters.status === 'todos' || user.status === filters.status
    const matchesRole = filters.role === 'todos' || user.role === filters.role
    const matchesApplication = filters.application === 'todos' || getUserApplicationCodes(user).includes(filters.application)

    return matchesSearch && matchesStatus && matchesRole && matchesApplication
  })
}

const UserInfoItem = ({ label, value }) => (
  <Box className="admin-user-info-item">
    <Typography>{label}</Typography>
    <Typography>{value || '-'}</Typography>
  </Box>
)

const AssignmentDateField = ({ label, value, onChange }) => (
  <Box className="admin-date-field">
    <Typography component="label">{label}</Typography>
    <TextField
      type="date"
      size="small"
      value={toDateInputValue(value)}
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ 'aria-label': label }}
      fullWidth
    />
  </Box>
)

const CreateUserDialog = ({
  open,
  onClose,
  onCreateUser,
  fallbackApplications,
  fallbackRoles,
  fallbackPermissions,
}) => {
  const [form, setForm] = React.useState(emptyCreateUserForm)
  const [applications, setApplications] = React.useState([])
  const [roles, setRoles] = React.useState([])
  const [permissions, setPermissions] = React.useState([])
  const [loadingOptions, setLoadingOptions] = React.useState(false)
  const [optionsError, setOptionsError] = React.useState('')
  const [submitError, setSubmitError] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)

  const applicationOptions = applications.length > 0 ? applications : fallbackApplications
  const roleOptions = roles.length > 0 ? roles : fallbackRoles
  const permissionOptions = permissions.length > 0 ? permissions : fallbackPermissions

  React.useEffect(() => {
    if (!open) return

    let active = true
    setForm(emptyCreateUserForm)
    setSubmitError('')
    setOptionsError('')

    if (API_REQUESTS_PAUSED) {
      setLoadingOptions(false)
      return undefined
    }

    setLoadingOptions(true)

    const loadOptions = async () => {
      try {
        const [applicationsResponse, rolesResponse, permissionsResponse] = await Promise.all([
          getApplications({ page: 1, limit: 100 }),
          getRoles({ page: 1, limit: 100 }),
          getPermissions({ page: 1, limit: 100 }),
        ])

        if (!active) return
        setApplications(getPaginatedItems(applicationsResponse))
        setRoles(getPaginatedItems(rolesResponse))
        setPermissions(getPaginatedItems(permissionsResponse))
      } catch (error) {
        if (active) setOptionsError(getApiErrorMessage(error, 'No se han podido cargar aplicaciones, roles y permisos.'))
      } finally {
        if (active) setLoadingOptions(false)
      }
    }

    loadOptions()

    return () => {
      active = false
    }
  }, [open])

  const handleTextChange = (event) => {
    const { name, value, checked, type } = event.target
    setForm((currentForm) => ({
      ...currentForm,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleApplicationSelect = (event) => {
    const selectedIds = event.target.value
    setForm((currentForm) => ({
      ...currentForm,
      applications: selectedIds.map((applicationId) => (
        currentForm.applications.find((application) => application.applicationId === applicationId) || {
          applicationId,
          active: true,
          validFrom: '',
          validUntil: '',
        }
      )),
    }))
  }

  const handleRoleSelect = (event) => {
    setForm((currentForm) => ({
      ...currentForm,
      roleIds: event.target.value,
    }))
  }

  const handlePermissionSelect = (event) => {
    const selectedIds = event.target.value
    setForm((currentForm) => ({
      ...currentForm,
      permissions: selectedIds.map((permissionId) => (
        currentForm.permissions.find((permission) => permission.permissionId === permissionId) || {
          permissionId,
          effect: 'ALLOW',
        }
      )),
    }))
  }

  const handleApplicationChange = (applicationId, field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      applications: currentForm.applications.map((application) => (
        application.applicationId === applicationId ? { ...application, [field]: value } : application
      )),
    }))
  }

  const handlePermissionEffectChange = (permissionId, effect) => {
    setForm((currentForm) => ({
      ...currentForm,
      permissions: currentForm.permissions.map((permission) => (
        permission.permissionId === permissionId ? { ...permission, effect } : permission
      )),
    }))
  }

  const findApplication = (applicationId) => applicationOptions.find((application) => String(application.id) === String(applicationId))
  const findRole = (roleId) => roleOptions.find((role) => String(role.id) === String(roleId))
  const findPermission = (permissionId) => permissionOptions.find((permission) => String(permission.id) === String(permissionId))

  const buildPayload = () => {
    const payload = {
      username: form.username.trim(),
      displayName: form.displayName.trim(),
      password: form.password,
      active: form.active,
      mustChangePassword: form.mustChangePassword,
      applications: form.applications.map((application) => ({
        applicationId: application.applicationId,
        active: application.active,
        ...(application.validFrom ? { validFrom: application.validFrom } : {}),
        ...(application.validUntil ? { validUntil: application.validUntil } : {}),
      })),
      roles: form.roleIds.map((roleId) => ({ roleId })),
      permissions: form.permissions.map((permission) => ({
        permissionId: permission.permissionId,
        effect: permission.effect,
      })),
    }

    if (form.email.trim()) payload.email = form.email.trim()
    return payload
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitError('')

    if (!form.username.trim()) {
      setSubmitError('El usuario es obligatorio.')
      return
    }

    if (!form.displayName.trim()) {
      setSubmitError('El nombre visible es obligatorio.')
      return
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setSubmitError('El correo electrónico no tiene un formato válido.')
      return
    }

    if (form.password.length < 8) {
      setSubmitError('La contraseña debe tener al menos 8 caracteres.')
      return
    }

    setSubmitting(true)
    try {
      await onCreateUser(buildPayload())
      onClose()
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, 'No se ha podido crear el usuario.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle className="admin-user-dialog-title">
          <Box>
            <Typography variant="h5">Nuevo usuario</Typography>
            <Typography>Alta de usuario, aplicaciones, roles y permisos directos.</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {loadingOptions && <LinearProgress />}
            {optionsError && <Alert severity="warning">{optionsError}</Alert>}
            {submitError && <Alert severity="error">{submitError}</Alert>}

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Datos principales</Typography>
              <Box className="admin-create-user-grid">
                <TextField label="Usuario" name="username" value={form.username} onChange={handleTextChange} size="small" required />
                <TextField label="Nombre visible" name="displayName" value={form.displayName} onChange={handleTextChange} size="small" required />
                <TextField label="Correo electrónico" name="email" value={form.email} onChange={handleTextChange} size="small" type="email" />
                <TextField label="Contraseña temporal" name="password" value={form.password} onChange={handleTextChange} size="small" type="password" required autoComplete="new-password" />
                <FormControlLabel control={<Checkbox checked={form.active} onChange={handleTextChange} name="active" />} label="Usuario activo" />
                <FormControlLabel control={<Checkbox checked={form.mustChangePassword} onChange={handleTextChange} name="mustChangePassword" />} label="Debe cambiar contraseña" />
              </Box>
            </Box>

            <Box className="admin-create-user-assignment-grid">
              <Box className="admin-user-dialog-section">
                <Typography className="section-title">Aplicaciones</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Aplicaciones asignadas</InputLabel>
                  <Select
                    multiple
                    value={form.applications.map((application) => application.applicationId)}
                    label="Aplicaciones asignadas"
                    onChange={handleApplicationSelect}
                    renderValue={(selected) => selected.map((applicationId) => getOptionLabel(findApplication(applicationId) || { id: applicationId })).join(', ')}
                  >
                    {applicationOptions.map((application) => (
                      <MenuItem key={application.id} value={String(application.id)}>
                        <Checkbox checked={form.applications.some((selected) => selected.applicationId === String(application.id))} />
                        <Box>
                          <Typography className="admin-create-user-option-title">{getOptionLabel(application)}</Typography>
                          <Typography className="admin-create-user-option-copy">{application.code || application.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box className="admin-create-user-selected-list">
                  {form.applications.length === 0 && <Typography className="admin-user-empty">Sin aplicaciones seleccionadas.</Typography>}
                  {form.applications.map((application) => {
                    const selectedApplication = findApplication(application.applicationId)
                    return (
                      <Box key={application.applicationId} className="admin-create-user-application-row">
                        <Box>
                          <Typography>{getOptionLabel(selectedApplication || { id: application.applicationId })}</Typography>
                          <Typography>{selectedApplication?.code || application.applicationId}</Typography>
                        </Box>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={application.active}
                              onChange={(event) => handleApplicationChange(application.applicationId, 'active', event.target.checked)}
                            />
                          )}
                          label="Activa"
                        />
                        <AssignmentDateField
                          label="Válida desde"
                          value={application.validFrom}
                          onChange={(value) => handleApplicationChange(application.applicationId, 'validFrom', value)}
                        />
                        <AssignmentDateField
                          label="Válida hasta"
                          value={application.validUntil}
                          onChange={(value) => handleApplicationChange(application.applicationId, 'validUntil', value)}
                        />
                      </Box>
                    )
                  })}
                </Box>
              </Box>

              <Box className="admin-user-dialog-section">
                <Typography className="section-title">Roles</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Roles asignados</InputLabel>
                  <Select
                    multiple
                    value={form.roleIds}
                    label="Roles asignados"
                    onChange={handleRoleSelect}
                    renderValue={(selected) => selected.map((roleId) => getOptionLabel(findRole(roleId) || { id: roleId })).join(', ')}
                  >
                    {roleOptions.map((role) => (
                      <MenuItem key={role.id} value={String(role.id)}>
                        <Checkbox checked={form.roleIds.includes(String(role.id))} />
                        <Box>
                          <Typography className="admin-create-user-option-title">{getOptionLabel(role)}</Typography>
                          <Typography className="admin-create-user-option-copy">{role.application?.code || role.code || role.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Permisos directos</Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Permisos</InputLabel>
                <Select
                  multiple
                  value={form.permissions.map((permission) => permission.permissionId)}
                  label="Permisos"
                  onChange={handlePermissionSelect}
                  renderValue={(selected) => selected.map((permissionId) => getOptionLabel(findPermission(permissionId) || { id: permissionId })).join(', ')}
                >
                  {permissionOptions.map((permission) => (
                    <MenuItem key={permission.id} value={String(permission.id)}>
                      <Checkbox checked={form.permissions.some((selected) => selected.permissionId === String(permission.id))} />
                      <Box>
                        <Typography className="admin-create-user-option-title">{getOptionLabel(permission)}</Typography>
                        <Typography className="admin-create-user-option-copy">{permission.code || permission.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box className="admin-create-user-permission-list">
                {form.permissions.length === 0 && <Typography className="admin-user-empty">Sin permisos directos seleccionados.</Typography>}
                {form.permissions.map((permission) => {
                  const selectedPermission = findPermission(permission.permissionId)
                  return (
                    <Box key={permission.permissionId} className="admin-create-user-permission-row">
                      <Box>
                        <Typography>{getOptionLabel(selectedPermission || { id: permission.permissionId })}</Typography>
                        <Typography>{selectedPermission?.code || permission.permissionId}</Typography>
                      </Box>
                      <FormControl size="small">
                        <InputLabel>Efecto</InputLabel>
                        <Select
                          value={permission.effect}
                          label="Efecto"
                          onChange={(event) => handlePermissionEffectChange(permission.permissionId, event.target.value)}
                        >
                          <MenuItem value="ALLOW">ALLOW</MenuItem>
                          <MenuItem value="DENY">DENY</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" startIcon={<AddRoundedIcon />} disabled={submitting}>
            {submitting ? 'Creando...' : 'Crear usuario'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

const UserEditDialog = ({
  user,
  open,
  onClose,
  onUpdateUser,
  onDeleteUser,
  fallbackApplications,
  fallbackRoles,
  fallbackPermissions,
}) => {
  const [form, setForm] = React.useState(emptyCreateUserForm)
  const [applications, setApplications] = React.useState([])
  const [roles, setRoles] = React.useState([])
  const [permissions, setPermissions] = React.useState([])
  const [loadingOptions, setLoadingOptions] = React.useState(false)
  const [optionsError, setOptionsError] = React.useState('')
  const [submitError, setSubmitError] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState('')

  const selectedUser = user?.raw || user
  const applicationOptions = applications.length > 0 ? applications : fallbackApplications
  const roleOptions = roles.length > 0 ? roles : fallbackRoles
  const permissionOptions = permissions.length > 0 ? permissions : fallbackPermissions

  React.useEffect(() => {
    if (!open || !selectedUser) return

    let active = true
    setForm(buildUserFormFromUser(selectedUser))
    setSubmitError('')
    setOptionsError('')
    setConfirmDelete(false)
    setDeleteError('')

    if (API_REQUESTS_PAUSED) {
      setLoadingOptions(false)
      return undefined
    }

    setLoadingOptions(true)

    const loadOptions = async () => {
      try {
        const [applicationsResponse, rolesResponse, permissionsResponse] = await Promise.all([
          getApplications({ page: 1, limit: 100 }),
          getRoles({ page: 1, limit: 100 }),
          getPermissions({ page: 1, limit: 100 }),
        ])

        if (!active) return
        setApplications(getPaginatedItems(applicationsResponse))
        setRoles(getPaginatedItems(rolesResponse))
        setPermissions(getPaginatedItems(permissionsResponse))
      } catch (error) {
        if (active) setOptionsError(getApiErrorMessage(error, 'No se han podido cargar aplicaciones, roles y permisos.'))
      } finally {
        if (active) setLoadingOptions(false)
      }
    }

    loadOptions()

    return () => {
      active = false
    }
  }, [open, selectedUser])

  if (!selectedUser) return null

  const handleTextChange = (event) => {
    const { name, value, checked, type } = event.target
    setForm((currentForm) => ({
      ...currentForm,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleApplicationSelect = (event) => {
    const selectedIds = event.target.value
    setForm((currentForm) => ({
      ...currentForm,
      applications: selectedIds.map((applicationId) => (
        currentForm.applications.find((application) => application.applicationId === applicationId) || {
          applicationId,
          active: true,
          validFrom: '',
          validUntil: '',
        }
      )),
    }))
  }

  const handleRoleSelect = (event) => {
    setForm((currentForm) => ({
      ...currentForm,
      roleIds: event.target.value,
    }))
  }

  const handlePermissionSelect = (event) => {
    const selectedIds = event.target.value
    setForm((currentForm) => ({
      ...currentForm,
      permissions: selectedIds.map((permissionId) => (
        currentForm.permissions.find((permission) => permission.permissionId === permissionId) || {
          permissionId,
          effect: 'ALLOW',
        }
      )),
    }))
  }

  const handleApplicationChange = (applicationId, field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      applications: currentForm.applications.map((application) => (
        application.applicationId === applicationId ? { ...application, [field]: value } : application
      )),
    }))
  }

  const handlePermissionEffectChange = (permissionId, effect) => {
    setForm((currentForm) => ({
      ...currentForm,
      permissions: currentForm.permissions.map((permission) => (
        permission.permissionId === permissionId ? { ...permission, effect } : permission
      )),
    }))
  }

  const findApplication = (applicationId) => applicationOptions.find((application) => String(application.id) === String(applicationId))
  const findRole = (roleId) => roleOptions.find((role) => String(role.id) === String(roleId))
  const findPermission = (permissionId) => permissionOptions.find((permission) => String(permission.id) === String(permissionId))

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitError('')

    const validationError = validateUserForm(form, false)
    if (validationError) {
      setSubmitError(validationError)
      return
    }

    setSubmitting(true)
    try {
      await onUpdateUser(selectedUser.id, buildUserPayload(form, Boolean(form.password)))
      onClose()
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, 'No se ha podido actualizar el usuario.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    setDeleteError('')
    setDeleting(true)
    try {
      await onDeleteUser(selectedUser.id)
      onClose()
    } catch (error) {
      setDeleteError(getApiErrorMessage(error, 'No se ha podido eliminar el usuario.'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle className="admin-user-dialog-title">
          <Box>
            <Typography variant="h5">{selectedUser.displayName || selectedUser.username}</Typography>
            <Typography>{selectedUser.username}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {loadingOptions && <LinearProgress />}
            {optionsError && <Alert severity="warning">{optionsError}</Alert>}
            {submitError && <Alert severity="error">{submitError}</Alert>}

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Datos principales</Typography>
              <Box className="admin-create-user-grid">
                <TextField label="Usuario" name="username" value={form.username} onChange={handleTextChange} size="small" required />
                <TextField label="Nombre visible" name="displayName" value={form.displayName} onChange={handleTextChange} size="small" required />
                <TextField label="Correo electrónico" name="email" value={form.email} onChange={handleTextChange} size="small" type="email" />
                <TextField label="Nueva contraseña" name="password" value={form.password} onChange={handleTextChange} size="small" type="password" autoComplete="new-password" helperText="Déjalo vacío para mantener la actual" />
                <FormControlLabel control={<Checkbox checked={form.active} onChange={handleTextChange} name="active" />} label="Usuario activo" />
                <FormControlLabel control={<Checkbox checked={form.mustChangePassword} onChange={handleTextChange} name="mustChangePassword" />} label="Debe cambiar contraseña" />
              </Box>
            </Box>

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Informacion de cuenta</Typography>
              <Box className="admin-user-info-grid">
                <UserInfoItem label="ID" value={selectedUser.id} />
                <UserInfoItem label="Intentos fallidos" value={selectedUser.failedLoginAttempts ?? 0} />
                <UserInfoItem label="Bloqueado hasta" value={formatDateTime(selectedUser.lockedUntil, 'No bloqueado')} />
                <UserInfoItem label="Ultimo acceso" value={formatDateTime(selectedUser.lastLoginAt)} />
                <UserInfoItem label="Creado" value={formatDateTime(selectedUser.createdAt, '-')} />
                <UserInfoItem label="Actualizado" value={formatDateTime(selectedUser.updatedAt, '-')} />
              </Box>
            </Box>

            <Box className="admin-create-user-assignment-grid">
              <Box className="admin-user-dialog-section">
                <Typography className="section-title">Aplicaciones</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Aplicaciones asignadas</InputLabel>
                  <Select
                    multiple
                    value={form.applications.map((application) => application.applicationId)}
                    label="Aplicaciones asignadas"
                    onChange={handleApplicationSelect}
                    renderValue={(selected) => selected.map((applicationId) => getOptionLabel(findApplication(applicationId) || { id: applicationId })).join(', ')}
                  >
                    {applicationOptions.map((application) => (
                      <MenuItem key={application.id} value={String(application.id)}>
                        <Checkbox checked={form.applications.some((selected) => selected.applicationId === String(application.id))} />
                        <Box>
                          <Typography className="admin-create-user-option-title">{getOptionLabel(application)}</Typography>
                          <Typography className="admin-create-user-option-copy">{application.code || application.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box className="admin-create-user-selected-list">
                  {form.applications.length === 0 && <Typography className="admin-user-empty">Sin aplicaciones seleccionadas.</Typography>}
                  {form.applications.map((application) => {
                    const selectedApplication = findApplication(application.applicationId)
                    return (
                      <Box key={application.applicationId} className="admin-create-user-application-row">
                        <Box>
                          <Typography>{getOptionLabel(selectedApplication || { id: application.applicationId })}</Typography>
                          <Typography>{selectedApplication?.code || application.applicationId}</Typography>
                        </Box>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={application.active}
                              onChange={(event) => handleApplicationChange(application.applicationId, 'active', event.target.checked)}
                            />
                          )}
                          label="Activa"
                        />
                        <AssignmentDateField
                          label="Válida desde"
                          value={application.validFrom}
                          onChange={(value) => handleApplicationChange(application.applicationId, 'validFrom', value)}
                        />
                        <AssignmentDateField
                          label="Válida hasta"
                          value={application.validUntil}
                          onChange={(value) => handleApplicationChange(application.applicationId, 'validUntil', value)}
                        />
                      </Box>
                    )
                  })}
                </Box>
              </Box>

              <Box className="admin-user-dialog-section">
                <Typography className="section-title">Roles</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Roles asignados</InputLabel>
                  <Select
                    multiple
                    value={form.roleIds}
                    label="Roles asignados"
                    onChange={handleRoleSelect}
                    renderValue={(selected) => selected.map((roleId) => getOptionLabel(findRole(roleId) || { id: roleId })).join(', ')}
                  >
                    {roleOptions.map((role) => (
                      <MenuItem key={role.id} value={String(role.id)}>
                        <Checkbox checked={form.roleIds.includes(String(role.id))} />
                        <Box>
                          <Typography className="admin-create-user-option-title">{getOptionLabel(role)}</Typography>
                          <Typography className="admin-create-user-option-copy">{role.application?.code || role.code || role.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box className="admin-user-dialog-section">
              <Typography className="section-title">Permisos directos</Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Permisos</InputLabel>
                <Select
                  multiple
                  value={form.permissions.map((permission) => permission.permissionId)}
                  label="Permisos"
                  onChange={handlePermissionSelect}
                  renderValue={(selected) => selected.map((permissionId) => getOptionLabel(findPermission(permissionId) || { id: permissionId })).join(', ')}
                >
                  {permissionOptions.map((permission) => (
                    <MenuItem key={permission.id} value={String(permission.id)}>
                      <Checkbox checked={form.permissions.some((selected) => selected.permissionId === String(permission.id))} />
                      <Box>
                        <Typography className="admin-create-user-option-title">{getOptionLabel(permission)}</Typography>
                        <Typography className="admin-create-user-option-copy">{permission.code || permission.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box className="admin-create-user-permission-list">
                {form.permissions.length === 0 && <Typography className="admin-user-empty">Sin permisos directos seleccionados.</Typography>}
                {form.permissions.map((permission) => {
                  const selectedPermission = findPermission(permission.permissionId)
                  return (
                    <Box key={permission.permissionId} className="admin-create-user-permission-row">
                      <Box>
                        <Typography>{getOptionLabel(selectedPermission || { id: permission.permissionId })}</Typography>
                        <Typography>{selectedPermission?.code || permission.permissionId}</Typography>
                      </Box>
                      <FormControl size="small">
                        <InputLabel>Efecto</InputLabel>
                        <Select
                          value={permission.effect}
                          label="Efecto"
                          onChange={(event) => handlePermissionEffectChange(permission.permissionId, event.target.value)}
                        >
                          <MenuItem value="ALLOW">ALLOW</MenuItem>
                          <MenuItem value="DENY">DENY</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )
                })}
              </Box>
            </Box>

            <Box className="admin-user-dialog-section admin-user-danger-zone">
              <Box>
                <Typography className="section-title">Eliminar usuario</Typography>
                <Typography className="admin-user-section-copy">
                  Esta accion eliminara el usuario y sus asignaciones de la suite.
                </Typography>
                {deleteError && <Alert severity="error">{deleteError}</Alert>}
              </Box>
              {!confirmDelete ? (
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Eliminar usuario
                </Button>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Typography className="admin-user-delete-confirm">Confirma la eliminacion de {selectedUser.username}.</Typography>
                  <Button variant="outlined" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                    Cancelar
                  </Button>
                  <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Eliminando...' : 'Confirmar eliminacion'}
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export const AdminUsers = () => {
  const [users, setUsers] = React.useState([])
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'todos',
    role: 'todos',
    application: 'todos',
  })
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [selectedUser, setSelectedUser] = React.useState(null)
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)

  const filteredUsers = React.useMemo(() => filterUsers(users, filters), [filters, users])
  const roleOptions = React.useMemo(() => getUniqueOptions(users.map((user) => user.role)), [users])
  const applicationOptions = React.useMemo(() => getUniqueOptions(users.flatMap(getUserApplicationCodes)), [users])
  const fallbackApplications = React.useMemo(() => getUserApplications(users), [users])
  const fallbackRoles = React.useMemo(() => getUserRoles(users), [users])
  const fallbackPermissions = React.useMemo(() => getUserPermissions(users), [users])

  const handleFilterChange = (event) => {
    const { name, value } = event.target
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }))
  }

  React.useEffect(() => {
    if (API_REQUESTS_PAUSED) return undefined

    let active = true

    const loadUsers = async () => {
      setLoading(true)
      setErrorMessage('')

      try {
        const response = await getUsers()
        const apiUsers = getPaginatedItems(response).map(mapApiUser)
        if (active) setUsers(apiUsers)
      } catch (error) {
        if (active) setErrorMessage(getApiErrorMessage(error))
      } finally {
        if (active) setLoading(false)
      }
    }

    loadUsers()

    return () => {
      active = false
    }
  }, [])

  const handleUpdateUser = async (id, payload) => {
    const response = await updateUser(id, payload)
    const updatedUser = response?.data || response

    if (updatedUser?.id) {
      const mappedUser = mapApiUser(updatedUser)
      setUsers((currentUsers) => currentUsers.map((user) => (
        String(user.id) === String(id) ? mappedUser : user
      )))
      setSelectedUser(mappedUser)
      return
    }

    try {
      const usersResponse = await getUsers()
      const apiUsers = getPaginatedItems(usersResponse).map(mapApiUser)
      setUsers(apiUsers)
      setSelectedUser(apiUsers.find((user) => String(user.id) === String(id)) || null)
    } catch {
      setUsers((currentUsers) => currentUsers.map((user) => (
        String(user.id) === String(id)
          ? {
            ...user,
            name: payload.displayName,
            username: payload.username,
            email: payload.email || '-',
            status: payload.active === false ? 'Inactivo' : 'Activo',
            apps: `${payload.applications?.length || 0} apps`,
            raw: {
              ...user.raw,
              username: payload.username,
              email: payload.email || '',
              displayName: payload.displayName,
              active: payload.active,
              mustChangePassword: payload.mustChangePassword,
            },
          }
          : user
      )))
    }
  }

  const handleCreateUser = async (payload) => {
    const response = await createUser(payload)
    const createdUser = response?.data || response

    if (createdUser?.id) {
      setUsers((currentUsers) => [mapApiUser(createdUser), ...currentUsers])
      return
    }

    const usersResponse = await getUsers()
    const apiUsers = getPaginatedItems(usersResponse).map(mapApiUser)
    setUsers(apiUsers)
  }

  const handleDeleteUser = async (id) => {
    await deleteUser(id)
    setUsers((currentUsers) => currentUsers.filter((user) => String(user.id) !== String(id)))
    setSelectedUser(null)
  }

  return (
    <Box className="page-fade">
      <Box className="page-heading suite-page-heading-row">
        <Box>
          <Typography variant="h4">Usuarios</Typography>
          <Typography>Administra usuarios, acceso a aplicaciones, estado de cuenta y sesiones recientes.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreateDialogOpen(true)}>Nuevo usuario</Button>
      </Box>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section">
        <Box className="admin-users-list-header">
          <Typography className="section-title">Listado de usuarios</Typography>
          <Chip className="suite-status-chip" label={`${filteredUsers.length} usuarios`} />
        </Box>
        <Box className="admin-users-filter-bar">
          <TextField
            label="Buscar usuario"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            size="small"
            className="admin-users-search"
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
            }}
          />
          <FormControl size="small" className="admin-users-filter">
            <InputLabel>Estado</InputLabel>
            <Select name="status" value={filters.status} label="Estado" onChange={handleFilterChange}>
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="Activo">Activos</MenuItem>
              <MenuItem value="Inactivo">Inactivos</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" className="admin-users-filter">
            <InputLabel>Rol</InputLabel>
            <Select name="role" value={filters.role} label="Rol" onChange={handleFilterChange}>
              <MenuItem value="todos">Todos</MenuItem>
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>{role}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" className="admin-users-filter">
            <InputLabel>Aplicacion</InputLabel>
            <Select name="application" value={filters.application} label="Aplicacion" onChange={handleFilterChange}>
              <MenuItem value="todos">Todas</MenuItem>
              {applicationOptions.map((application) => (
                <MenuItem key={application} value={application}>{application}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}
        {errorMessage && <Alert severity="warning" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}
        <Box className="activity-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol principal</TableCell>
                <TableCell>Aplicaciones</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Ultimo acceso</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow
                  component={AnimatedTableRow}
                  key={user.id || user.username}
                  hover
                  className="admin-clickable-row"
                  onClick={() => setSelectedUser(user)}
                >
                  <TableCell>
                    <Box className="activity-app-cell">
                      <Box className="activity-row-icon">{user.icon}</Box>
                      <Box>
                        <Typography>{user.name}</Typography>
                        <Typography className="admin-table-subtext">{user.username}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.apps}</TableCell>
                  <TableCell>
                    <Chip size="small" className={`admin-status-chip ${user.status === 'Activo' ? 'admin-status-success' : 'suite-status-chip-muted'}`} label={user.status} />
                  </TableCell>
                  <TableCell>{user.lastLogin}</TableCell>
                  <TableCell align="right"><ArrowForwardRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography className="admin-user-empty">No hay usuarios que coincidan con los filtros.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box></AnimatedSection>

      <UserEditDialog
        user={selectedUser}
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        onUpdateUser={handleUpdateUser}
        onDeleteUser={handleDeleteUser}
        fallbackApplications={fallbackApplications}
        fallbackRoles={fallbackRoles}
        fallbackPermissions={fallbackPermissions}
      />
      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreateUser={handleCreateUser}
        fallbackApplications={fallbackApplications}
        fallbackRoles={fallbackRoles}
        fallbackPermissions={fallbackPermissions}
      />
    </Box>
  )
}
